/**
 * BadgeUploadModal — paylaşımlı rozet yükleme modalı.
 * Badges sayfasında ve Periods form'unda kullanılır.
 *
 * Props:
 *   open        {boolean}   — modal açık mı
 *   uploading   {boolean}   — upload devam ediyor mu (spinner + disable)
 *   onConfirm   {(file: File, name: string) => void}
 *   onCancel    {() => void}
 *   onFileError {(msg: string) => void} — boyut/format hatası
 */
import { useEffect, useRef, useState } from 'react';

import Button from './Button';
import { Icon } from './Icon';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB

function nameFromFile(filename) {
  return (filename || '')
    .replace(/\.[^.]+$/, '')
    .replace(/[_\-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function BadgeUploadModal({ open, uploading, onConfirm, onCancel, onFileError }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // modal kapanınca state'i sıfırla
  useEffect(() => {
    if (!open) {
      setFile(null);
      setName('');
      setPreview(null);
      setDragOver(false);
    }
  }, [open]);

  // önizleme URL yönetimi
  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!open) return null;

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > MAX_FILE_SIZE) {
      onFileError?.('Dosya boyutu 2 MB sınırını aşıyor.');
      return;
    }
    setFile(f);
    setName(nameFromFile(f.name));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={() => !uploading && onCancel()} />

      <div className="relative z-10 w-full max-w-sm rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-4">
          <h2 className="text-sm font-semibold text-neutral-900">Rozet Yükle</h2>
          <button
            type="button"
            onClick={() => !uploading && onCancel()}
            className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
          >
            <Icon name="x" className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Gizli file input */}
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            className="hidden"
            onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
          />

          {!file ? (
            /* Drop zone */
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={[
                'w-full rounded-xl border-2 border-dashed px-4 py-10 text-center transition-colors',
                dragOver
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-neutral-200 bg-neutral-50 hover:border-primary-300 hover:bg-primary-50/50',
              ].join(' ')}
            >
              <Icon name="upload" className="mx-auto mb-2 h-8 w-8 text-neutral-300" />
              <p className="text-sm font-medium text-neutral-600">Dosya seçin veya sürükleyip bırakın</p>
              <p className="mt-1 text-[11px] text-neutral-400">PNG · JPG · GIF · WebP · maks. 2 MB</p>
            </button>
          ) : (
            /* Dosya seçildi: önizleme + değiştir */
            <div className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-3">
              <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg border border-neutral-200 bg-white flex items-center justify-center">
                <img src={preview} alt="önizleme" className="h-full w-full object-contain p-1" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-neutral-700">{file.name}</p>
                <p className="text-[11px] text-neutral-400">{(file.size / 1024).toFixed(0)} KB</p>
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className="mt-1 text-[11px] font-medium text-primary-600 hover:text-primary-700"
                >
                  Değiştir
                </button>
              </div>
            </div>
          )}

          {/* İsim alanı — her zaman görünür */}
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700">
              Rozet Adı <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="örn. Altın Kupa"
              maxLength={255}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-neutral-100 px-5 py-4">
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={uploading}>
            İptal
          </Button>
          <Button
            icon="upload"
            size="sm"
            loading={uploading}
            disabled={!file || !name.trim()}
            onClick={() => onConfirm(file, name.trim())}
          >
            Yükle
          </Button>
        </div>
      </div>
    </div>
  );
}
